import { useState, useEffect } from 'react'
import {Star, ThumbsUp} from 'lucide-react'
import { lumi } from '../lib/lumi'
import { useAuth } from '../hooks/useAuth'
import toast from 'react-hot-toast'

interface RatingComponentProps {
  couponId: string
}

interface Rating {
  _id: string
  userId: string
  rating: number
  review?: string
  userEmail: string
  helpful: number
  createdAt: string
  verified: boolean
}

export default function RatingComponent({ couponId }: RatingComponentProps) {
  const { user } = useAuth()
  const [ratings, setRatings] = useState<Rating[]>([])
  const [averageRating, setAverageRating] = useState(0)
  const [totalRatings, setTotalRatings] = useState(0)
  const [userRating, setUserRating] = useState<Rating | null>(null)
  const [showReviewForm, setShowReviewForm] = useState(false)
  const [formData, setFormData] = useState({
    rating: 5,
    review: ''
  })

  useEffect(() => {
    loadRatings()
  }, [couponId])

  const loadRatings = async () => {
    try {
      const result = await lumi.entities.couponRatings.list({
        filter: { couponId },
        sort: { createdAt: -1 }
      })

      const ratingsData = result.list || []
      setRatings(ratingsData)
      setTotalRatings(ratingsData.length)

      if (ratingsData.length > 0) {
        const avg = ratingsData.reduce((sum, r) => sum + r.rating, 0) / ratingsData.length
        setAverageRating(Math.round(avg * 10) / 10)
      }

      if (user) {
        const myRating = ratingsData.find(r => r.userId === user.userId)
        setUserRating(myRating || null)
      }
    } catch (error) {
      console.error('Error loading ratings:', error)
    }
  }

  const handleSubmitRating = async () => {
    if (!user) {
      toast.error('Please sign in to rate deals')
      return
    }

    try {
      if (userRating) {
        // Update existing rating
        await lumi.entities.couponRatings.update(userRating._id, {
          rating: formData.rating,
          review: formData.review,
          updatedAt: new Date().toISOString()
        })
        toast.success('Rating updated!')
      } else {
        // Create new rating
        await lumi.entities.couponRatings.create({
          couponId,
          userId: user.userId,
          userEmail: user.email || '',
          rating: formData.rating,
          review: formData.review,
          helpful: 0,
          verified: false,
          creator: user.userId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        })
        toast.success('Rating submitted!')
      }

      setShowReviewForm(false)
      loadRatings()
    } catch (error) {
      console.error('Error submitting rating:', error)
      toast.error('Failed to submit rating')
    }
  }

  const handleDeleteRating = async () => {
    if (!userRating) return

    if (confirm('Delete your rating?')) {
      try {
        await lumi.entities.couponRatings.delete(userRating._id)
        toast.success('Rating deleted')
        setUserRating(null)
        setShowReviewForm(false)
        loadRatings()
      } catch (error) {
        console.error('Error deleting rating:', error)
        toast.error('Failed to delete rating')
      }
    }
  }

  const renderStars = (rating: number, interactive = false, onChange?: (rating: number) => void) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-5 h-5 ${
              star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
            } ${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}`}
            onClick={() => interactive && onChange && onChange(star)}
          />
        ))}
      </div>
    )
  }

  return (
    <div className="mt-6 border-t pt-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900">User Reviews</h3>
          {totalRatings > 0 && (
            <div className="flex items-center gap-2 mt-1">
              {renderStars(averageRating)}
              <span className="text-sm text-gray-600">
                {averageRating} ({totalRatings} {totalRatings === 1 ? 'review' : 'reviews'})
              </span>
            </div>
          )}
        </div>
        
        {user && (
          <button
            onClick={() => {
              setShowReviewForm(!showReviewForm)
              if (userRating && !showReviewForm) {
                setFormData({
                  rating: userRating.rating,
                  review: userRating.review || ''
                })
              }
            }}
            className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
          >
            {userRating ? 'Edit Review' : 'Write Review'}
          </button>
        )}
      </div>

      {showReviewForm && (
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">Your Rating</label>
            {renderStars(formData.rating, true, (rating) => setFormData({ ...formData, rating }))}
          </div>
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">Review (optional)</label>
            <textarea
              value={formData.review}
              onChange={(e) => setFormData({ ...formData, review: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              rows={3}
              placeholder="Share your experience with this deal..."
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSubmitRating}
              className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
            >
              {userRating ? 'Update' : 'Submit'}
            </button>
            {userRating && (
              <button
                onClick={handleDeleteRating}
                className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            )}
            <button
              onClick={() => setShowReviewForm(false)}
              className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {ratings.length === 0 && (
          <p className="text-gray-500 text-sm text-center py-4">
            No reviews yet. Be the first to review this deal!
          </p>
        )}

        {ratings.map((rating) => (
          <div key={rating._id} className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-start justify-between mb-2">
              <div>
                {renderStars(rating.rating)}
                <p className="text-sm text-gray-600 mt-1">
                  {rating.userEmail.split('@')[0]} • {new Date(rating.createdAt).toLocaleDateString()}
                </p>
              </div>
              {rating.verified && (
                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                  Verified
                </span>
              )}
            </div>
            {rating.review && (
              <p className="text-gray-700 text-sm mt-2">{rating.review}</p>
            )}
            <div className="flex items-center gap-2 mt-3">
              <button className="flex items-center gap-1 text-sm text-gray-500 hover:text-green-600">
                <ThumbsUp className="w-4 h-4" />
                <span>Helpful {rating.helpful > 0 && `(${rating.helpful})`}</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
